import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider, useTheme } from "@/lib/theme";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Chatbot from "@/components/Chatbot";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import DemoModeBanner from "@/components/DemoModeBanner";
import Landing from "@/pages/Landing";
import Listings from "@/pages/Listings";
import MapCatalog from "@/pages/MapCatalog";
import PropertyDetail from "@/pages/PropertyDetail";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Pubblica from "@/pages/Pubblica";
import PropertyEdit from "@/pages/PropertyEdit";
import AgencyProfile from "@/pages/AgencyProfile";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import Favorites from "@/pages/Favorites";
import Chat from "@/pages/Chat";
import Invoices from "@/pages/Invoices";
import Admin from "@/pages/Admin";
import ConsentPage from "@/pages/ConsentPage";
import ListingStatusUpdate from "@/pages/ListingStatusUpdate";
import TransferRequestPage from "@/pages/TransferRequestPage";
import AnalyticsPro from "@/pages/AnalyticsPro";
import AgencyFeedConfig from "@/pages/AgencyFeedConfig";
import SoldShowcase from "@/pages/SoldShowcase";
import TutorialPage from "@/pages/TutorialPage";
import AgencyCalendar from "@/pages/AgencyCalendar";
import TeamInvite from "@/pages/TeamInvite";
import ClientPortal from "@/pages/portal/ClientPortal";
import Privacy from "@/pages/Privacy";
import CookiePolicy from "@/pages/CookiePolicy";
import Terms from "@/pages/Terms";
import Onboarding from "@/pages/Onboarding";
import CookieConsent from "@/components/CookieConsent";
import LegalAcceptanceGate from "@/components/LegalAcceptanceGate";
import ZoneOperativeGate from "@/components/ZoneOperativeGate";
import VendiCasa from "@/pages/VendiCasa";
import CityPage from "@/pages/CityPage";
import ScopriVendidirect from "@/pages/ScopriVendidirect";
import SeoLandingPage from "@/pages/SeoLandingPage";
import { isSeoLandingSlug } from "@/lib/seoLanding";
import { ScrollToTop } from "@/components/ScrollToTop";

// URL geografici /case-{città} (es. /case-milano) — segmento parziale non supportato da react-router, gestito qui.
function CatchAll() {
    const { pathname } = useLocation();
    const seoSlug = pathname.replace(/^\/+|\/+$/g, "").toLowerCase();
    if (isSeoLandingSlug(seoSlug)) return <SeoLandingPage slug={seoSlug} />;
    const m = pathname.match(/^\/case-([a-z0-9-]+)\/?$/i);
    if (m) return <CityPage slug={m[1].toLowerCase()} />;
    return <Landing />;
}

function AppToaster() {
    const { isDark } = useTheme();
    return (
        <Toaster
            theme={isDark ? "dark" : "light"}
            position="bottom-right"
            toastOptions={{
                style: isDark
                    ? { background: "#0F0F0F", border: "1px solid rgba(212,175,106,0.45)", color: "#FAFAF7" }
                    : { background: "#FFFFFF", border: "1px solid rgba(194,155,87,0.5)", color: "#1A1A1A" },
            }}
        />
    );
}

function AppRouter() {
    const location = useLocation();
    // Landing di vendita isolata: nessuna Navbar/Footer/Chatbot/banner del sito principale
    if (location.pathname === "/scopri-vendidirect") {
        return <ScopriVendidirect />;
    }
    return (
        <>
            <ImpersonationBanner />
            <Navbar />
            <DemoModeBanner />
            <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/annunci" element={<Listings />} />
                <Route path="/annunci/mappa" element={<MapCatalog />} />
                <Route path="/annunci/:id" element={<PropertyDetail />} />
                <Route path="/annunci/:id/modifica" element={<PropertyEdit />} />
                <Route path="/agenzie/:id" element={<AgencyProfile />} />
                <Route path="/agenzia/:slug" element={<AgencyProfile />} />
                <Route path="/prezzi" element={<Pricing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/registrazione" element={<Register />} />
                <Route path="/password-dimenticata" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profilo" element={<Profile />} />
                <Route path="/pubblica" element={<Pubblica />} />
                <Route path="/preferiti" element={<Favorites />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/fatture" element={<Invoices />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/consenso" element={<ConsentPage />} />
                <Route path="/vendi-casa" element={<VendiCasa />} />
                <Route path="/aggiorna-annuncio" element={<ListingStatusUpdate />} />
                <Route path="/trasferimento" element={<TransferRequestPage />} />
                <Route path="/analytics-pro" element={<AnalyticsPro />} />
                <Route path="/agenzia/feed" element={<AgencyFeedConfig />} />
                <Route path="/venduti" element={<SoldShowcase />} />
                <Route path="/tutorial" element={<TutorialPage />} />
                <Route path="/agenzia/calendario" element={<AgencyCalendar />} />
                <Route path="/team/invito/:token" element={<TeamInvite />} />
                <Route path="/portale/:token" element={<ClientPortal />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookie" element={<CookiePolicy />} />
                <Route path="/termini" element={<Terms />} />
                <Route path="/abbonamento/success" element={<SubscriptionSuccess />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="*" element={<CatchAll />} />
            </Routes>
            <Footer />
            <Chatbot />
            <CookieConsent />
            <LegalAcceptanceGate />
            <ZoneOperativeGate />
            <AppToaster />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <AppRouter />
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </div>
    );
}

export default App;
